import {
	inject,
	provide,
	type InjectionKey,
	type Ref,
	type ShallowRef,
	type WritableComputedRef,
} from 'vue';
import type { Client, ChannelStream } from '@ts-ssh2/core';
import type { PanelSystem } from './use-panels';
import type { FileNode, OpenFile } from './use-sftp';
import type { SelectionState } from './use-virtual-list';
import type { LayoutState } from './use-workspace';

// everything the workspace provides to panel components
export type WorkspaceContext = {
	panels: PanelSystem;
	// ssh
	client: ShallowRef<Client | null>;
	hasShell: Ref<boolean>;
	hasSftp: Ref<boolean>;
	openShell: (options?: { cols: number; rows: number }) => Promise<ChannelStream>;
	disconnect: () => void;
	// sftp
	rootNode: Ref<FileNode | null>;
	rootPath: Ref<string>;
	initializing: Ref<boolean>;
	sftpError: Ref<string | null>;
	selectionState: SelectionState;
	toggleDirectory: (node: FileNode) => void;
	openFile: (node: FileNode) => void;
	changeRoot: (path: string) => void;
	clearError: () => void;
	// file reading
	readFile: (path: string, onProgress?: (percent: number) => void) => Promise<Uint8Array | null>;
	// file editing
	openFiles: Ref<OpenFile[]>;
	activeFilePath: Ref<string | null>;
	openFilePath: (path: string, onProgress?: (percent: number) => void) => Promise<boolean>;
	updateFileContent: (path: string, content: string) => void;
	saveFile: (path: string, onProgress?: (percent: number) => void) => void;
	closeFile: (path: string) => void;
	// workspace persistence
	getPanelState: <T>(uri: string) => T | null;
	setPanelState: <T>(uri: string, state: T) => void;
	layout: WritableComputedRef<LayoutState>;
};

const WORKSPACE_KEY: InjectionKey<WorkspaceContext> = Symbol('workspace');

export function provideWorkspace(ctx: WorkspaceContext) {
	provide(WORKSPACE_KEY, ctx);
}

export function useWorkspaceContext(): WorkspaceContext {
	const ctx = inject(WORKSPACE_KEY);
	if (!ctx) throw new Error('useWorkspaceContext() called outside of workspace provider');
	return ctx;
}
